import React, { useEffect, useState } from 'react';
import { Card, Badge, BackButton, RNLLoader } from '../UI';
import Pagination from '../requisition/Pagination';
import { fmt, fmtDate } from '../../utils';
import * as api from '../../api';
import { pvStatusColor, pvStatusLabel, reviewStatusColor, STATUS_BADGE_STYLE } from './pvUtils';
import EyeButton from './EyeButton';
import PrintButton from './PrintButton';
import DownloadPDFButton from './DownloadPDFButton';
import VoucherDetailView from './VoucherDetailView';
import VoucherReviewDetail from './VoucherReviewDetail';

const th = { padding: '10px 12px', textAlign: 'center', fontSize: 10.5, fontWeight: 800, border: '1px solid black' };
const td = { padding: '9px 12px', textAlign: 'center', fontSize: 12.5, border: '1px solid black' };

export default function PostedVoucherRegister({ onBack, toast, openVoucherId, onOpenVoucher }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState({});
  const [details, setDetails] = useState({}); // payment_voucher_id -> { loading, items, requisition, error }
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Which voucher is open. Controlled when the caller supplies openVoucherId,
  // uncontrolled otherwise — aliased to the same names so every reference in
  // this file is unchanged either way.
  const [viewingIdState, setViewingIdState] = useState(null);
  const viewingId = openVoucherId !== undefined ? openVoucherId : viewingIdState;
  const setViewingId = onOpenVoucher || setViewingIdState;

  useEffect(() => {
    (async () => {
      setLoading(true);
      setExpandedIds({});
      try {
        api.clearApiCache();
        const res = await api.getPostedVoucherRegister(page.skip, page.limit, search);
        setRows(res?.vouchers || []);
        setTotalCount(res?.total_count ?? 0);
      } catch (e) {
        toast.show(e.message, 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [toast, page, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage({ ...page, skip: 0 });
    setSearch(searchInput.trim());
  };

  const toggleRow = async (row) => {
    const id = row.payment_voucher_id;
    const willExpand = !expandedIds[id];
    setExpandedIds(prev => ({ ...prev, [id]: willExpand }));
    if (willExpand && !details[id]) {
      setDetails(prev => ({ ...prev, [id]: { loading: true } }));
      try {
        const voucher = await api.getPaymentVoucher(id);
        let tracking = null;
        // The register row has no workflow_instance_id of its own — the
        // voucher does, so the status history is reachable without widening
        // the register response.
        if (voucher?.workflow_instance_id) {
          try {
            tracking = await api.getWorkflowTracking(voucher.workflow_instance_id);
          } catch { /* supplementary: the panel still renders without it */ }
        }
        setDetails(prev => ({
          ...prev,
          [id]: { voucher, tracking, history: tracking?.approval_history || [] },
        }));
      } catch (e) {
        toast.show(e.message, 'error');
        setDetails(prev => ({ ...prev, [id]: { error: `Could not load this voucher: ${e.message}` } }));
      }
    }
  };

  if (viewingId) {
    return (
      <VoucherDetailView
        voucherId={viewingId}
        onBack={() => setViewingId(null)}
        toast={toast}
        showWorkflowActions={false}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Only shown when opened as an overlay. As a top-level tab there is
          nothing to go "back" to — the tab bar is already the way out. */}
      {onBack && (
        <div>
          <BackButton onClick={onBack} style={{ marginBottom: 12 }}>← Back to Payment Voucher Engine</BackButton>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Voucher Register</h2>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Every payment voucher ever raised company-wide, newest first. This is a permanent record — nothing disappears once approved, rejected, or posted.
          </div>
        </div>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search voucher no., REQ no., supplier…"
            style={{
              padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6,
              fontSize: 13, width: 280, outline: 'none',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '8px 16px', background: '#0b318b', color: '#fff',
              border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600,
            }}
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearch(''); setPage({ ...page, skip: 0 }); }}
              style={{
                padding: '8px 12px', background: '#f1f5f9', color: '#475569',
                border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Card style={{ border: '1px solid black', borderBottom: (!loading && rows.length > 0) ? 'none' : '1px solid black', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><RNLLoader title="Loading posted vouchers…" /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No vouchers have been posted yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', border: '1px solid black' }}>
              <thead>
                <tr style={{ background: '#0b318b', color: '#fff' }}>
                  <th style={{ ...th, width: 36 }} />
                  <th style={th}>VOUCHER NO.</th>
                  <th style={th}>REQ NO.</th>
                  <th style={th}>DATE</th>
                  <th style={th}>RAISED BY</th>
                  <th style={th}>SUPPLIER</th>
                  <th style={th}>CURRENTLY WITH</th>
                  <th style={th}>NET PAYABLE</th>
                  <th style={th}>STATUS</th>
                  <th style={th}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const id = row.payment_voucher_id;
                  const expanded = !!expandedIds[id];
                  return (
                    <React.Fragment key={id}>
                      <tr onClick={() => toggleRow(row)} style={{ cursor: 'pointer', background: expanded ? '#dbeafe' : (idx % 2 === 0 ? '#fff' : '#f8fafc') }}>
                        <td style={{ ...td, color: '#64748b' }}>{expanded ? '▼' : '▶'}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{row.voucher_no || '—'}</td>
                        <td style={{ ...td, fontWeight: 600, color: '#0b318b' }}>{row.purchase_request_pr_number || '—'}</td>
                        <td style={td}>{fmtDate(row.voucher_date)}</td>
                        <td style={td}>{row.created_by_name || '—'}</td>
                        <td style={td}>{row.supplier_name || '—'}</td>
                        {/* Once a voucher is finished it is not on anyone's
                            desk, so an em-dash is the honest answer — "Not
                            Assigned" implies it is still waiting for someone. */}
                        <td style={td}>{row.currently_with || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{fmt(row.net_payable)}</td>
                        {/* The derived review status, not the raw workflow
                            status — a released voucher is still "approved"
                            underneath, which read as APPROVED here. */}
                        <td style={td}>
                          <Badge
                            color={row.review_status ? reviewStatusColor(row.review_status) : pvStatusColor(row.status)}
                            pulse={row.review_status === 'Awaiting Review'}
                            style={STATUS_BADGE_STYLE}
                          >
                            {row.review_status || pvStatusLabel(row.status)}
                          </Badge>
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <EyeButton onClick={(e) => { e.stopPropagation(); setViewingId(id); }} />
                            <PrintButton voucherId={id} toast={toast} />
                            <DownloadPDFButton voucherId={id} voucherNo={row.voucher_no} toast={toast} />
                          </div>
                        </td>
                      </tr>

                      {expanded && (
                        <tr>
                          {/* The same panel the approval queue expands, not a
                              second rendering of the same voucher. Two layouts
                              for one record drift: a field added to one is
                              missing from the other, and the register is the
                              permanent audit trail — the place least able to
                              afford a partial view. */}
                          <td colSpan={10} style={{ padding: 0, border: '1px solid black' }}>
                            <VoucherReviewDetail {...(details[id] || { loading: true })} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {!loading && rows.length > 0 && (
        <div style={{ borderTop: '1px solid black', borderLeft: '2.5px solid black', borderRight: '2.5px solid black', borderBottom: '2.5px solid black', background: 'var(--bg-card)' }}>
          <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
        </div>
      )}
      </div>
    </div>
  );
}
